import { useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  adminApi,
  type AdminProviderAccount,
  type AdminRejectReason,
  type AdminVenueReview,
  type ProviderApprovalStatus,
  type VenueApprovalStatus,
} from "../../../lib/api";
import { consoleApi, type ConsoleTournament, type TournamentReviewStatus } from "../../../lib/admin-console-api";
import { useAuth } from "../../../lib/auth";
import { useAppPreferences } from "../../../lib/preferences";
import { ProviderDecision, REJECT_REASONS, type ProviderDecisionInput } from "../ProviderDecision";
import { errorMessage, LoadState, PageHeader, StatusChip, useFormatters, useLoad, useNotices } from "../ui";

type QueueType = "providers" | "venues" | "removals" | "tournaments";

const TABS: Array<{ id: QueueType; label: string }> = [
  { id: "providers", label: "Provider registrations" },
  { id: "venues", label: "New venues" },
  { id: "removals", label: "Venue removals" },
  { id: "tournaments", label: "Tournaments" },
];

const PROVIDER_FILTERS: Array<{ id: "actionable" | "all" | ProviderApprovalStatus; label: string }> = [
  { id: "actionable", label: "Needs action" },
  { id: "rejected", label: "Rejected" },
  { id: "all", label: "All" },
];

const VENUE_FILTERS: Array<{ id: "actionable" | "rejected" | "all"; label: string }> = [
  { id: "actionable", label: "Needs action" },
  { id: "rejected", label: "Rejected" },
  { id: "all", label: "All" },
];

export function ApprovalsPage() {
  const { t } = useAppPreferences();
  const [params, setParams] = useSearchParams();
  const type = (TABS.some((tab) => tab.id === params.get("type")) ? params.get("type") : "providers") as QueueType;
  const setType = (next: QueueType) => setParams({ type: next }, { replace: false });

  return (
    <div className="console-page">
      <PageHeader
        description={t("Decide who goes live. Every decision is recorded with your name.")}
        kicker={t("Work")}
        title={t("Approvals")}
      />
      <div aria-label={t("Approval queues")} className="console-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            aria-selected={type === tab.id}
            className="console-tab"
            key={tab.id}
            onClick={() => setType(tab.id)}
            role="tab"
            type="button"
          >
            {t(tab.label)}
          </button>
        ))}
      </div>
      {type === "providers" ? <ProviderQueue /> : type === "tournaments" ? <TournamentQueue /> : <VenueQueue removals={type === "removals"} />}
    </div>
  );
}

export function ProviderQueue() {
  const { t } = useAppPreferences();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { dateTime } = useFormatters();
  const [params, setParams] = useSearchParams();
  const filter = (params.get("status") ?? "actionable") as "actionable" | "all" | ProviderApprovalStatus;
  const { banners, setError, setNotice } = useNotices();
  const providers = useLoad(() => adminApi.listProviders().then((response) => response.providers), []);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [bulkNote, setBulkNote] = useState("");
  const [bulkReason, setBulkReason] = useState<AdminRejectReason | "">("");
  const pending = useRef(false);

  const all = providers.data ?? [];
  const visible = useMemo(
    () =>
      all.filter((provider) =>
        filter === "all" ? true : filter === "actionable" ? provider.approval_status === "pending" : provider.approval_status === filter,
      ),
    [all, filter],
  );
  const selectable = visible.filter((provider) => provider.approval_status === "pending").map((provider) => provider.id);

  const merge = (updated: AdminProviderAccount[]) => {
    const byId = new Map(updated.map((provider) => [provider.id, provider]));
    providers.setData((current) => (current ?? []).map((provider) => byId.get(provider.id) ?? provider));
  };

  const decide = async (provider: AdminProviderAccount, input: ProviderDecisionInput) => {
    if (pending.current) return;
    pending.current = true;
    setBusyId(provider.id);
    setError(null);
    setNotice(null);
    try {
      const updated = await adminApi.updateProviderStatus(provider.id, {
        approval_status: input.status,
        review_note: input.note.trim() || null,
        reason_code: input.status === "rejected" || input.status === "suspended" ? input.reason || null : null,
      });
      merge([updated]);
      setSelected((current) => current.filter((id) => id !== provider.id));
      setNotes((current) => ({ ...current, [provider.id]: "" }));
      setNotice(t("{name} is now {status}.", { name: updated.business_name, status: t(updated.approval_status) }));
    } catch (error) {
      setError(errorMessage(error, t("The decision was not saved. Your note is kept; try again.")));
    } finally {
      pending.current = false;
      setBusyId(null);
    }
  };

  const bulk = async (status: ProviderApprovalStatus) => {
    if (pending.current || selected.length === 0) return;
    if (status === "rejected" && (!bulkNote.trim() || !bulkReason)) {
      setError(t("Add a note and pick a reason before rejecting."));
      return;
    }
    pending.current = true;
    setBusyId("bulk");
    setError(null);
    setNotice(null);
    try {
      const response = await adminApi.bulkProviderStatus({
        approval_status: status,
        provider_ids: selected,
        reason_code: status === "rejected" ? bulkReason || null : null,
        review_note: status === "rejected" ? bulkNote.trim() : null,
      });
      merge(response.providers);
      setSelected([]);
      setBulkNote("");
      setBulkReason("");
      setNotice(t("Updated {count} providers.", { count: response.providers.length }));
    } catch (error) {
      setError(errorMessage(error, t("The selected providers were not updated.")));
    } finally {
      pending.current = false;
      setBusyId(null);
    }
  };

  return (
    <div className="grid gap-3">
      {banners}
      <div className="console-toolbar">
        <div className="console-pills">
          {PROVIDER_FILTERS.map((item) => (
            <button
              aria-pressed={filter === item.id}
              className="ops-filter-pill"
              data-active={filter === item.id}
              key={item.id}
              onClick={() => {
                setSelected([]);
                setParams({ type: "providers", status: item.id });
              }}
              type="button"
            >
              {t(item.label)}
            </button>
          ))}
        </div>
        {isAdmin && selectable.length > 0 ? (
          <label className="console-checkbox">
            <input
              checked={selectable.every((id) => selected.includes(id))}
              onChange={() => setSelected((current) => (selectable.every((id) => current.includes(id)) ? [] : selectable))}
              type="checkbox"
            />
            {t("Select all")}
          </label>
        ) : null}
        {isAdmin ? <Link className="ops-button" data-size="sm" data-variant="secondary" to="/admin/providers/new">{t("Add provider")}</Link> : null}
      </div>

      {isAdmin && selected.length > 0 ? (
        <div className="ops-subtle-surface console-bulk-bar">
          <span className="console-muted">{t("{count} selected", { count: selected.length })}</span>
          <input
            aria-label={t("Shared note for the selected providers")}
            className="ops-input"
            onChange={(event) => setBulkNote(event.target.value)}
            placeholder={t("Shared note (needed to reject)")}
            value={bulkNote}
          />
          <select
            aria-label={t("Shared reason")}
            className="ops-select"
            onChange={(event) => setBulkReason(event.target.value as AdminRejectReason | "")}
            value={bulkReason}
          >
            <option value="">{t("Reason")}</option>
            {REJECT_REASONS.map((item) => <option key={item.value} value={item.value}>{t(item.label)}</option>)}
          </select>
          <div className="console-button-row">
            <button className="ops-button" data-size="sm" data-variant="primary" disabled={busyId !== null} onClick={() => void bulk("approved")} type="button">
              {t("Approve selected")}
            </button>
            <button className="ops-button" data-size="sm" data-variant="danger" disabled={busyId !== null} onClick={() => void bulk("rejected")} type="button">
              {t("Reject selected")}
            </button>
          </div>
        </div>
      ) : null}

      <LoadState
        empty={filter === "actionable" ? t("No provider registrations are waiting.") : t("No providers match this filter.")}
        error={providers.error}
        isEmpty={visible.length === 0}
        loading={providers.loading}
        onRetry={() => void providers.reload()}
      >
        <ul className="console-queue">
          {visible.map((provider) => (
            <li className="ops-surface console-queue-item" data-status={provider.approval_status} key={provider.id}>
              <div className="console-queue-head">
                {isAdmin && provider.approval_status === "pending" ? (
                  <input
                    aria-label={t("Select {name}", { name: provider.business_name })}
                    checked={selected.includes(provider.id)}
                    disabled={busyId !== null}
                    onChange={() =>
                      setSelected((current) => (current.includes(provider.id) ? current.filter((id) => id !== provider.id) : [...current, provider.id]))
                    }
                    type="checkbox"
                  />
                ) : null}
                <Link className="console-queue-title" to={`/admin/providers/${provider.id}`}>{provider.business_name}</Link>
                <StatusChip status={provider.approval_status} />
                <span className="console-muted">{provider.city} · {t("Registered {date}", { date: dateTime(provider.created_at) })}</span>
              </div>
              <p className="console-queue-meta">
                {provider.contact_name ?? "—"} · <span className="console-truncate">{provider.contact_email}</span> · {provider.contact_phone ?? t("No phone")}
              </p>
              {provider.application_note ? <p className="console-quote">{provider.application_note}</p> : null}
              {provider.review_note && provider.approval_status !== "pending" ? (
                <p className="console-muted">{t("Last note: {note}", { note: provider.review_note })}</p>
              ) : null}
              <ProviderDecision
                busy={busyId !== null}
                isAdmin={isAdmin}
                note={notes[provider.id] ?? ""}
                onDecide={(input) => void decide(provider, input)}
                onNoteChange={(value) => setNotes((current) => ({ ...current, [provider.id]: value }))}
                provider={provider}
              />
            </li>
          ))}
        </ul>
      </LoadState>
    </div>
  );
}

function VenueQueue({ removals }: { removals: boolean }) {
  const { t } = useAppPreferences();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { dateTime } = useFormatters();
  const [params, setParams] = useSearchParams();
  const filter = (params.get("status") ?? "actionable") as "actionable" | "rejected" | "all";
  const { banners, setError, setNotice } = useNotices();
  const venues = useLoad(() => adminApi.listVenues().then((response) => response.venues), []);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const target: VenueApprovalStatus = removals ? "removal_requested" : "pending";

  const visible = (venues.data ?? []).filter((venue) => {
    if (removals) return filter === "all" ? ["removal_requested", "removed"].includes(venue.approval_status) : venue.approval_status === (filter === "rejected" ? "removed" : target);
    if (filter === "all") return venue.approval_status !== "removal_requested" && venue.approval_status !== "removed";
    return venue.approval_status === (filter === "rejected" ? "rejected" : target);
  });
  const selectable = visible.filter((venue) => venue.approval_status === target).map((venue) => venue.id);

  const merge = (updated: AdminVenueReview[]) => {
    const byId = new Map(updated.map((venue) => [venue.id, venue]));
    venues.setData((current) => (current ?? []).map((venue) => byId.get(venue.id) ?? venue));
  };

  const decide = async (venue: AdminVenueReview, decision: "approve" | "reject") => {
    setBusyId(venue.id);
    setError(null);
    setNotice(null);
    try {
      const updated = await adminApi.updateVenueStatus(venue.id, { decision, review_note: notes[venue.id]?.trim() || null });
      merge([updated]);
      setNotes((current) => ({ ...current, [venue.id]: "" }));
      setNotice(t("{name} is now {status}.", { name: updated.name, status: t(updated.approval_status.replaceAll("_", " ")) }));
    } catch (error) {
      setError(errorMessage(error, t("The decision was not saved. Your note is kept; try again.")));
    } finally {
      setBusyId(null);
    }
  };

  const bulk = async (decision: "approve" | "reject") => {
    if (selected.length === 0) return;
    setBusyId("bulk");
    setError(null);
    try {
      const response = await adminApi.bulkVenueStatus({ venue_ids: selected, decision, review_note: null });
      merge(response.venues);
      setSelected([]);
      setNotice(t("Updated {count} venues.", { count: response.venues.length }));
    } catch (error) {
      setError(errorMessage(error, t("The selected venues were not updated.")));
    } finally {
      setBusyId(null);
    }
  };

  const approveLabel = removals ? t("Approve removal") : t("Approve");
  const rejectLabel = removals ? t("Keep the venue") : t("Reject");

  return (
    <div className="grid gap-3">
      {banners}
      <div className="console-toolbar">
        <div className="console-pills">
          {VENUE_FILTERS.map((item) => (
            <button
              aria-pressed={filter === item.id}
              className="ops-filter-pill"
              data-active={filter === item.id}
              key={item.id}
              onClick={() => {
                setSelected([]);
                setParams({ type: removals ? "removals" : "venues", status: item.id });
              }}
              type="button"
            >
              {item.id === "rejected" && removals ? t("Removed") : t(item.label)}
            </button>
          ))}
        </div>
        {isAdmin && selectable.length > 1 ? (
          <label className="console-checkbox">
            <input
              checked={selectable.every((id) => selected.includes(id))}
              onChange={() => setSelected((current) => (selectable.every((id) => current.includes(id)) ? [] : selectable))}
              type="checkbox"
            />
            {t("Select all")}
          </label>
        ) : null}
      </div>
      {isAdmin && selected.length > 0 ? (
        <div className="ops-subtle-surface console-bulk-bar">
          <span className="console-muted">{t("{count} selected", { count: selected.length })}</span>
          <div className="console-button-row">
            <button className="ops-button" data-size="sm" data-variant="primary" disabled={busyId !== null} onClick={() => void bulk("approve")} type="button">{approveLabel}</button>
            <button className="ops-button" data-size="sm" data-variant="danger" disabled={busyId !== null} onClick={() => void bulk("reject")} type="button">{rejectLabel}</button>
          </div>
        </div>
      ) : null}
      <LoadState
        empty={removals ? t("No removal requests are waiting.") : t("No new venues are waiting.")}
        error={venues.error}
        isEmpty={visible.length === 0}
        loading={venues.loading}
        onRetry={() => void venues.reload()}
      >
        <ul className="console-queue">
          {visible.map((venue) => {
            const actionable = venue.approval_status === target || (!removals && venue.approval_status === "rejected");
            return (
              <li className="ops-surface console-queue-item" data-status={venue.approval_status} key={venue.id}>
                <div className="console-queue-head">
                  {isAdmin && venue.approval_status === target ? (
                    <input
                      aria-label={t("Select {name}", { name: venue.name })}
                      checked={selected.includes(venue.id)}
                      onChange={() => setSelected((current) => (current.includes(venue.id) ? current.filter((id) => id !== venue.id) : [...current, venue.id]))}
                      type="checkbox"
                    />
                  ) : null}
                  <Link className="console-queue-title" to={`/admin/venues/${venue.id}`}>{venue.name}</Link>
                  <StatusChip status={venue.approval_status} />
                  <span className="console-muted">
                    <Link className="console-link" to={`/admin/providers/${venue.provider_id}`}>{venue.provider_name}</Link> · {venue.city} · {dateTime(venue.created_at)}
                  </span>
                </div>
                <p className="console-queue-meta">{venue.address}</p>
                {venue.latitude === null || venue.longitude === null ? (
                  <p className="console-warning">{t("No map pin yet. The owner must add one before approval.")}</p>
                ) : null}
                {venue.submission_note ? <p className="console-quote">{t("Owner's note: {note}", { note: venue.submission_note })}</p> : null}
                {venue.removal_note ? <p className="console-quote">{t("Removal reason: {note}", { note: venue.removal_note })}</p> : null}
                {venue.review_note && !actionable ? <p className="console-muted">{t("Last note: {note}", { note: venue.review_note })}</p> : null}
                {actionable ? (
                  <div className="console-decision">
                    <label className="ops-field">
                      <span className="ops-label">{t("Note to the owner")}</span>
                      <textarea
                        className="ops-textarea"
                        onChange={(event) => setNotes((current) => ({ ...current, [venue.id]: event.target.value }))}
                        rows={2}
                        value={notes[venue.id] ?? ""}
                      />
                    </label>
                    <div className="console-button-row">
                      <button className="ops-button" data-size="sm" data-variant="primary" disabled={busyId !== null} onClick={() => void decide(venue, "approve")} type="button">
                        {approveLabel}
                      </button>
                      {venue.approval_status !== "rejected" ? (
                        <button className="ops-button" data-size="sm" data-variant="danger" disabled={busyId !== null} onClick={() => void decide(venue, "reject")} type="button">
                          {rejectLabel}
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </LoadState>
    </div>
  );
}

const TOURNAMENT_FILTERS: Array<{ id: TournamentReviewStatus | "all"; label: string }> = [
  { id: "pending", label: "Needs action" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Declined" },
  { id: "all", label: "All" },
];

export function TournamentQueue() {
  const { t, resolvedLanguage } = useAppPreferences();
  const { dateTime } = useFormatters();
  const [params, setParams] = useSearchParams();
  const requested = params.get("status");
  const filter = (TOURNAMENT_FILTERS.some((item) => item.id === requested) ? requested : "pending") as TournamentReviewStatus | "all";
  const { banners, setError, setNotice } = useNotices();
  const tournaments = useLoad(() => consoleApi.listTournaments(filter).then((response) => response.tournaments), [filter]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  // Event times are shown in the venue's own timezone, like the venue sees them.
  const eventTime = (value: string, timeZone: string) =>
    new Intl.DateTimeFormat(resolvedLanguage === "ar" ? "ar-EG" : "en-GB", { dateStyle: "medium", timeStyle: "short", timeZone }).format(new Date(value));

  const decide = async (tournament: ConsoleTournament, decision: "approve" | "reject") => {
    const note = notes[tournament.id]?.trim() ?? "";
    if (decision === "reject" && !note) {
      setError(t("Tell the venue what to change before declining."));
      return;
    }
    setBusyId(tournament.id);
    setError(null);
    setNotice(null);
    try {
      const updated = await consoleApi.reviewTournament(tournament.id, {
        decision,
        review_note: note || null,
        expected_updated_at: tournament.updated_at,
      });
      tournaments.setData((current) =>
        (current ?? [])
          .map((item) => (item.id === updated.id ? updated : item))
          .filter((item) => filter === "all" || item.review_status === filter),
      );
      setNotes((current) => ({ ...current, [tournament.id]: "" }));
      setNotice(decision === "approve" ? t("{name} is now visible to players.", { name: updated.name }) : t("{name} was sent back to the venue.", { name: updated.name }));
    } catch (error) {
      setError(errorMessage(error, t("The decision was not saved. Your note is kept; try again.")));
      void tournaments.reload();
    } finally {
      setBusyId(null);
    }
  };

  const list = tournaments.data ?? [];
  return (
    <div className="grid gap-3">
      {banners}
      <div className="console-toolbar">
        <div className="console-pills">
          {TOURNAMENT_FILTERS.map((item) => (
            <button
              aria-pressed={filter === item.id}
              className="ops-filter-pill"
              data-active={filter === item.id}
              key={item.id}
              onClick={() => setParams({ type: "tournaments", status: item.id })}
              type="button"
            >
              {t(item.label)}
            </button>
          ))}
        </div>
      </div>
      <LoadState
        empty={filter === "pending" ? t("No tournaments are waiting.") : t("No tournaments match this filter.")}
        error={tournaments.error}
        isEmpty={list.length === 0}
        loading={tournaments.loading}
        onRetry={() => void tournaments.reload()}
      >
        <ul className="console-queue">
          {list.map((tournament) => {
            const live = tournament.status === "published";
            const canApprove = live && tournament.review_status === "pending";
            const canDecline = live && (tournament.review_status === "pending" || tournament.review_status === "approved");
            const note = notes[tournament.id] ?? "";
            return (
              <li className="ops-surface console-queue-item" data-status={tournament.review_status ?? undefined} key={tournament.id}>
                <div className="console-queue-head">
                  <span className="console-queue-title">{tournament.name}</span>
                  {tournament.status === "cancelled" ? <StatusChip status="cancelled" /> : <StatusChip status={tournament.review_status ?? "pending"} />}
                  <span className="console-muted">
                    <Link className="console-link" to={`/admin/venues/${tournament.venue_id}`}>{tournament.venue_name}</Link>
                    {" · "}
                    <Link className="console-link" to={`/admin/providers/${tournament.provider_id}`}>{tournament.provider_name}</Link>
                    {" · "}{tournament.venue_city}
                  </span>
                </div>
                <p className="console-queue-meta">
                  {tournament.game} · {eventTime(tournament.starts_at, tournament.venue_timezone)} – {eventTime(tournament.ends_at, tournament.venue_timezone)}
                </p>
                <dl className="console-tournament-details">
                  {tournament.description ? <div><dt>{t("About")}</dt><dd>{tournament.description}</dd></div> : null}
                  {tournament.rules ? <div><dt>{t("Rules & entry terms")}</dt><dd>{tournament.rules}</dd></div> : null}
                  <div><dt>{t("How to enter")}</dt><dd>{tournament.registration_instructions ?? "—"}</dd></div>
                  <div><dt>{t("Contact phone")}</dt><dd dir="ltr">{tournament.contact_phone ?? "—"}</dd></div>
                </dl>
                <p className="console-muted">
                  {t("Sent by {name} {date}", { name: tournament.created_by_name ?? t("the venue"), date: dateTime(tournament.submitted_at) })}
                  {tournament.reviewed_by_name && tournament.review_status !== "pending"
                    ? ` · ${t("Reviewed by {name} {date}", { name: tournament.reviewed_by_name, date: dateTime(tournament.reviewed_at) })}`
                    : null}
                </p>
                {tournament.last_review_note ? (
                  <p className="console-quote">
                    {tournament.review_status === "pending"
                      ? t("Declined before: {note}", { note: tournament.last_review_note })
                      : t("Last note: {note}", { note: tournament.last_review_note })}
                  </p>
                ) : null}
                {canApprove || canDecline ? (
                  <div className="console-decision">
                    <label className="ops-field">
                      <span className="ops-label">{canApprove ? t("Note to the venue (needed to decline)") : t("Why take it down? The venue sees this.")}</span>
                      <textarea
                        className="ops-textarea"
                        maxLength={1000}
                        onChange={(event) => setNotes((current) => ({ ...current, [tournament.id]: event.target.value }))}
                        rows={2}
                        value={note}
                      />
                    </label>
                    <div className="console-button-row">
                      {canApprove ? (
                        <button className="ops-button" data-size="sm" data-variant="primary" disabled={busyId !== null} onClick={() => void decide(tournament, "approve")} type="button">
                          {t("Approve")}
                        </button>
                      ) : null}
                      <button
                        className="ops-button"
                        data-size="sm"
                        data-variant="danger"
                        disabled={busyId !== null || !note.trim()}
                        onClick={() => void decide(tournament, "reject")}
                        type="button"
                      >
                        {canApprove ? t("Decline") : t("Take down")}
                      </button>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </LoadState>
    </div>
  );
}
